import {
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  RobotOutlined,
  StopOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  getToolName,
  isToolUIPart,
  type DynamicToolUIPart,
  type ToolUIPart,
  type UIMessage,
} from "ai";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Drawer, FloatButton, List, message, Popconfirm, Space, Tag, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { type AgentChatMessage, type AgentChatSession } from "@fitness-app/data-models";
import {
  trainingExerciseProposalSchema,
  trainingProgramProposalSchema,
} from "@fitness-app/data-models/entities/TrainingProgramAgent";
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@fitness-app/web-ui/components/ai-elements/conversation";
import { Loader } from "@fitness-app/web-ui/components/ai-elements/loader";
import { Message, MessageContent } from "@fitness-app/web-ui/components/ai-elements/message";
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputTextarea,
  PromptInputTools,
} from "@fitness-app/web-ui/components/ai-elements/prompt-input";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { mapStoredMessage } from "~/modules/Sales/SalesAgent/messageUtils";
import { SalesAgentMessageParts, SalesAgentPromptSubmit } from "~/modules/Sales/SalesAgent/SalesAgentMessageParts";
import { supabase } from "~/store/initializeStore";

import { ExerciseProposalCard } from "./ExerciseProposalCard";
import { TrainingProgramProposalCard } from "./TrainingProgramProposalCard";

const agentType = "training_program_agent";
const sessionsKey = ["trainingProgramAgentSessions"];

const ProposalToolPart = ({
  onExerciseSaved,
  part,
}: {
  onExerciseSaved: (exerciseName: string) => void;
  part: ToolUIPart | DynamicToolUIPart;
}) => {
  if (part.state === "output-available") {
    const proposal = trainingProgramProposalSchema.safeParse(part.output);
    if (proposal.success) return <TrainingProgramProposalCard proposal={proposal.data} />;
    const exerciseProposal = trainingExerciseProposalSchema.safeParse(part.output);
    if (exerciseProposal.success) {
      return <ExerciseProposalCard proposal={exerciseProposal.data} onSaved={onExerciseSaved} />;
    }
  }

  const failed = part.state === "output-error" || part.state === "output-denied";
  return (
    <div
      className={`my-2 rounded-lg border px-3 py-2 text-xs ${failed ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}`}
    >
      <Space>
        <ToolOutlined />
        <span>{getToolName(part).replaceAll("-", " ")}</span>
        <Tag color={failed ? "red" : part.state === "output-available" ? "green" : "blue"}>
          {part.state === "output-available" ? "Gotowe" : failed ? "Błąd" : "Pracuję"}
        </Tag>
      </Space>
    </div>
  );
};

const TrainingProgramMessageParts = ({
  message,
  isLastMessage,
  onExerciseSaved,
  streaming,
}: {
  message: UIMessage;
  isLastMessage: boolean;
  onExerciseSaved: (exerciseName: string) => void;
  streaming: boolean;
}) => (
  <>
    {message.parts.filter(isToolUIPart).map((part, index) => (
      <ProposalToolPart key={`${message.id}-tool-${index}`} part={part} onExerciseSaved={onExerciseSaved} />
    ))}
    <SalesAgentMessageParts message={message} isLastMessage={isLastMessage} isStreaming={streaming} />
  </>
);

export const TrainingProgramAgentDrawer = ({
  bubbleOffset = 0,
  open,
  onClose,
  onOpen,
}: {
  bubbleOffset?: number;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
}) => {
  const queryClient = useQueryClient();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [hasUnread, setHasUnread] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;
  const activeSessionIdRef = useRef<string | null>(null);
  // Blocks the stored-messages hydration effect while a reply for the given session is
  // in flight / just finished — otherwise a stale (or empty, for a fresh session) query
  // result wipes the streamed conversation the moment `busy` flips to false.
  const skipHydrationSessionIdRef = useRef<string | null>(null);
  const skipHydrationTokenRef = useRef(0);
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  const [input, setInput] = useState("");

  const { data: sessions } = useQuery({
    queryKey: sessionsKey,
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_chat_session")
        .select("*")
        .eq("agentType", agentType)
        .order("lastMessageAt", { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatSession[];
    },
  });

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${getClientWebApiBaseUrl()}/api/trainers/ai/training-program-chat`,
        fetch: async (request, init) => {
          const response = await fetch(request, init);
          const sessionId = response.headers.get("X-Agent-Chat-Session-Id");
          if (sessionId) {
            activeSessionIdRef.current = sessionId;
            skipHydrationSessionIdRef.current = sessionId;
            setActiveSessionId(sessionId);
            void queryClient.invalidateQueries({ queryKey: sessionsKey });
          }
          return response;
        },
        prepareSendMessagesRequest: async ({ api, body, headers, credentials, messages }) => {
          const token = (await supabase.auth.getSession()).data.session?.access_token;
          const latestMessage = messages.at(-1);
          return {
            api,
            credentials,
            headers: {
              ...headers,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: {
              ...body,
              sessionId: activeSessionIdRef.current ?? undefined,
              messages: latestMessage ? [latestMessage] : [],
            },
          };
        },
      }),
    [queryClient],
  );

  const { messages, sendMessage, setMessages, status, stop, error, clearError } = useChat<UIMessage>({
    transport,
    experimental_throttle: 50,
    onFinish: async () => {
      if (!openRef.current) setHasUnread(true);
      const skipHydrationToken = skipHydrationTokenRef.current;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: sessionsKey }),
        activeSessionIdRef.current
          ? queryClient.invalidateQueries({
              queryKey: ["trainingProgramAgentMessages", activeSessionIdRef.current],
            })
          : Promise.resolve(),
      ]);
      if (skipHydrationTokenRef.current === skipHydrationToken) {
        skipHydrationSessionIdRef.current = null;
      }
    },
  });
  const busy = status === "submitted" || status === "streaming";

  useEffect(() => {
    if (open) setHasUnread(false);
  }, [open]);

  const { data: storedMessages } = useQuery({
    queryKey: ["trainingProgramAgentMessages", activeSessionId],
    enabled: Boolean(activeSessionId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_chat_message")
        .select("*")
        .eq("sessionId", activeSessionId!)
        .order("createdAt", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatMessage[];
    },
  });

  useEffect(() => {
    if (!storedMessages || busy) return;
    if (activeSessionId && skipHydrationSessionIdRef.current === activeSessionId) return;
    setMessages(storedMessages.map(mapStoredMessage));
  }, [activeSessionId, busy, setMessages, storedMessages]);

  const selectSession = useCallback(
    (sessionId: string) => {
      activeSessionIdRef.current = sessionId;
      skipHydrationTokenRef.current += 1;
      skipHydrationSessionIdRef.current = null;
      setActiveSessionId(sessionId);
      setMessages([]);
      clearError();
    },
    [clearError, setMessages],
  );

  const startNew = useCallback(() => {
    activeSessionIdRef.current = null;
    skipHydrationTokenRef.current += 1;
    skipHydrationSessionIdRef.current = null;
    setActiveSessionId(null);
    setMessages([]);
    clearError();
  }, [clearError, setMessages]);

  const submit = useCallback(() => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    clearError();
    skipHydrationTokenRef.current += 1;
    skipHydrationSessionIdRef.current = activeSessionIdRef.current;
    void sendMessage({
      id: crypto.randomUUID(),
      role: "user",
      parts: [{ type: "text", text }],
    });
  }, [busy, clearError, input, sendMessage]);

  const handlePromptKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;

    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  }, []);

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error: deleteError } = await supabase.from("agent_chat_session").delete().eq("id", sessionId);
      if (deleteError) throw new Error(deleteError.message);
    },
    onSuccess: async (_, sessionId) => {
      if (sessionId === activeSessionIdRef.current) startNew();
      await queryClient.invalidateQueries({ queryKey: sessionsKey });
      void message.success("Rozmowa usunięta");
    },
    onError: () => void message.error("Nie udało się usunąć rozmowy"),
  });

  const handleExerciseSaved = useCallback((exerciseName: string) => {
    setInput(`Dodałem ćwiczenie „${exerciseName}” do bazy. Wyszukaj je ponownie i zaktualizuj propozycję planu.`);
    promptRef.current?.focus();
  }, []);

  const showFab = !open && (busy || messages.length > 0);

  return (
    <>
      {showFab ? (
        <FloatButton
          type="primary"
          icon={busy ? <LoadingOutlined /> : <RobotOutlined />}
          badge={busy ? { dot: true, color: "gold" } : hasUnread ? { dot: true, color: "green" } : undefined}
          tooltip={
            busy
              ? "Agent przygotowuje odpowiedź — kliknij, aby wrócić do rozmowy"
              : hasUnread
                ? "Nowa odpowiedź agenta"
                : "Wróć do rozmowy z agentem"
          }
          style={bubbleOffset ? { insetInlineEnd: 24, bottom: 24 + bubbleOffset } : undefined}
          onClick={onOpen}
        />
      ) : null}
      <Drawer
        open={open}
        onClose={onClose}
        width="min(1180px, 96vw)"
        title="Agent planów treningowych"
        destroyOnClose={false}
      >
        <div className="flex h-[calc(100vh-150px)] min-h-0 gap-4">
          <aside className="w-64 shrink-0 overflow-hidden rounded-lg border border-gray-200">
            <div className="border-b border-gray-100 p-3">
              <Button block icon={<PlusOutlined />} onClick={startNew}>
                Nowa rozmowa
              </Button>
            </div>
            <List
              size="small"
              className="h-[calc(100%-57px)] overflow-y-auto"
              dataSource={sessions ?? []}
              locale={{ emptyText: "Brak zapisanych rozmów" }}
              renderItem={(session) => (
                <List.Item
                  className={`group cursor-pointer px-3 hover:bg-gray-50 ${activeSessionId === session.id ? "bg-blue-50" : ""}`}
                  onClick={() => selectSession(session.id)}
                >
                  <Typography.Text ellipsis>{session.title}</Typography.Text>
                  <Popconfirm
                    title="Usunąć rozmowę?"
                    description="Wiadomości zostaną trwale usunięte."
                    okText="Usuń"
                    okButtonProps={{ danger: true }}
                    cancelText="Anuluj"
                    onPopupClick={(event) => event.stopPropagation()}
                    onConfirm={() => deleteSessionMutation.mutate(session.id)}
                  >
                    <Button
                      size="small"
                      type="text"
                      danger
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                      icon={<DeleteOutlined />}
                      loading={deleteSessionMutation.isLoading && deleteSessionMutation.variables === session.id}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </Popconfirm>
                </List.Item>
              )}
            />
          </aside>

          <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200">
            <Conversation>
              <ConversationContent className="mx-auto w-full max-w-4xl px-5 py-5">
                {messages.length ? (
                  messages.map((message, index) => (
                    <Message key={message.id} from={message.role}>
                      <MessageContent
                        className={
                          message.role === "user"
                            ? "rounded-[18px] bg-gray-100 px-4 py-2.5"
                            : "border-transparent bg-transparent px-0 py-0 shadow-none"
                        }
                      >
                        <TrainingProgramMessageParts
                          message={message}
                          isLastMessage={index === messages.length - 1}
                          streaming={status === "streaming" && index === messages.length - 1}
                          onExerciseSaved={handleExerciseSaved}
                        />
                      </MessageContent>
                    </Message>
                  ))
                ) : (
                  <ConversationEmptyState
                    icon={<RobotOutlined className="text-2xl" />}
                    title="Zaprojektuj plan treningowy"
                    description="Opisz cel, częstotliwość, dostępny sprzęt i ograniczenia. Agent dobierze ćwiczenia istniejące w bazie i przygotuje draft szablonu."
                  />
                )}
                {status === "submitted" ? <Loader /> : null}
                {error ? <Typography.Text type="danger">{error.message}</Typography.Text> : null}
              </ConversationContent>
              <ConversationScrollButton />
            </Conversation>

            <div className="shrink-0 bg-transparent px-4 pb-4 pt-0">
              <PromptInput
                onSubmit={submit}
                className="relative mx-auto max-w-4xl rounded-[28px] border-gray-200 bg-white/95 shadow-[0_12px_40px_rgba(15,23,42,0.08)] backdrop-blur-xl"
              >
                <PromptInputBody>
                  <PromptInputTextarea
                    ref={promptRef}
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={handlePromptKeyDown}
                    placeholder="Np. przygotuj domowy plan uzupełniający siłownię i piłkę…"
                    disabled={busy}
                  />
                </PromptInputBody>
                <PromptInputFooter>
                  <PromptInputTools />
                  {busy ? (
                    <Button
                      className="h-10 w-10 rounded-full bg-gray-950 text-white hover:bg-gray-800"
                      icon={<StopOutlined />}
                      onClick={stop}
                    >
                      <span className="sr-only">Stop</span>
                    </Button>
                  ) : (
                    <SalesAgentPromptSubmit input={input} status={status} />
                  )}
                </PromptInputFooter>
              </PromptInput>
            </div>
          </section>
        </div>
      </Drawer>
    </>
  );
};
